import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

export default function BarraPesquisa({
  placeholder = "Pesquisar...",
  onSearch,
  className = "",
  defaultValue = "",
  expandedWidth = 320,
  collapsedSize = 44,
  panelMaxHeight = 480,
  panelWidth,
  ContentComponent,
  contentProps = {},
  renderContent,
}) {
  const [value, setValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(Boolean(defaultValue));
  const [manuallyClosed, setManuallyClosed] = useState(false); // <<< NOVO: força fechar
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // expandido só se não estiver fechado manualmente
  const isExpanded = (isFocused || value.length > 0) && !manuallyClosed;

  // posição vertical do painel
  const [panelTop, setPanelTop] = useState(0);
  const updatePanelTop = () => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPanelTop(rect.bottom + window.scrollY + 8); // 8px gap
  };

  useLayoutEffect(() => {
    if (isExpanded) {
      updatePanelTop();
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;
    const onResizeOrScroll = () => updatePanelTop();
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll);
    };
  }, [isExpanded]);

  // bloqueia scroll do body enquanto aberto
  useEffect(() => {
    if (!isExpanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isExpanded]);

  // clique fora (fecha SEMPRE, independente de texto/pesquisa)
  useEffect(() => {
    function handleClickOutside(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setIsFocused(false);
        setManuallyClosed(true);   // <- força colapsar mesmo com texto
        inputRef.current?.blur();
        // setValue("");           // <- OPCIONAL: descomente se quiser também limpar o texto
      }
    }
    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("touchstart", handleClickOutside);
    };
  }, []); // <- dica: não precisa depender de value.length


  function handleKeyDown(e) {
    if (e.key === "Enter") {
      onSearch?.(value.trim());
      if (value.trim().length === 0) {
        setIsFocused(false);
        setManuallyClosed(false);
      }
    }
    if (e.key === "Escape") {
      if (value.length > 0) {
        setValue("");
      } else {
        setIsFocused(false);
        setManuallyClosed(false);
      }
      inputRef.current?.blur();
    }
  }

  function handleIconClick() {
    if (!isExpanded) {
      setManuallyClosed(false);              // reabre se estava fechado manualmente
      setIsFocused(true);
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      onSearch?.(value.trim());
    }
  }

  const widthStyle = {
    width: isExpanded
      ? typeof expandedWidth === "number"
        ? `${expandedWidth}px`
        : expandedWidth
      : `${collapsedSize}px`,
  };

  const closePanel = () => {
    setIsFocused(false);       // tira o foco
    setManuallyClosed(true);   // força colapsar
    setValue("");              // limpa o campo
    inputRef.current?.blur();  // remove foco real
  };


  // sempre que o usuário digita/clica na barra, consideramos que quer reabrir
  const onFocusOrInput = () => {
    if (manuallyClosed) setManuallyClosed(false);
    setIsFocused(true);
  };

  return (
    <div
      ref={containerRef}
      className={`bp-wrap ${className}`}
      style={{
        position: "relative",
        display: "inline-block",
        zIndex: 1001, // barra acima do overlay e painel
      }}
    >
      {/* Barra */}
      <div
        className="bp-container"
        style={{
          ...widthStyle,
          height: collapsedSize,
          transition: "width 180ms ease",
          display: "inline-flex",
          alignItems: "center",
          borderRadius: collapsedSize / 2,
          boxShadow: "0 1px 2px rgba(0,0,0,.06), 0 1px 10px rgba(0,0,0,.04)",
          background: "#fff",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          gap: 6,
          paddingRight: 6, // dá respiro pros botões da direita
        }}
        onFocus={onFocusOrInput}
        onClick={() => {
          onFocusOrInput();
          requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        {/* Botão lupa (abre/dispara busca) */}
        <button
          type="button"
          aria-label="Buscar"
          onClick={handleIconClick}
          style={{
            width: collapsedSize,
            height: collapsedSize,
            display: "grid",
            placeItems: "center",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            outline: "none",
            flex: "0 0 auto",
          }}
        >
          <LupaIcon size={18} />
        </button>

        {/* Input */}
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => { setValue(e.target.value); if (manuallyClosed) setManuallyClosed(false); }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          aria-label={placeholder}
          style={{
            flex: 1,
            height: "100%",
            border: "none",
            outline: "none",
            fontSize: 14,
            padding: "0 6px",
            background: "transparent",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 140ms ease",
            minWidth: 0,
          }}
        />

        {/* Botão "limpar" (cinza), só aparece se houver texto */}
        {value.length > 0 && (
          <button
            type="button"
            aria-label="Limpar busca"
            title="Limpar"
            onClick={() => {
              setValue("");
              setManuallyClosed(false);
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            style={{
              width: collapsedSize - 8,
              height: collapsedSize - 8,
              display: "grid",
              placeItems: "center",
              border: "none",
              background: "#f3f4f6",
              color: "#374151",
              borderRadius: (collapsedSize - 8) / 2,
              cursor: "pointer",
              outline: "none",
              flex: "0 0 auto",
            }}
          >
            <FecharIcon size={14} />
          </button>
        )}

      </div>

      {/* Overlay + Painel via Portal */}
      {isExpanded &&
        createPortal(
          <>
            {/* Overlay escuro (fica atrás da barra) */}
            <div
              onClick={() => {
                // fecha SEMPRE
                setIsFocused(false);
                setManuallyClosed(true); // <- garante colapso mesmo com texto digitado
                inputRef.current?.blur();
                // setValue("");         // <- OPCIONAL: limpe o texto se quiser
              }}
              style={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.45)",
                backdropFilter: "blur(2px)",
                zIndex: 999, // abaixo da barra (1001)
              }}
            />
            {/* Painel centralizado 80% */}
            <div
              className="bp-panel"
              style={{
                position: "fixed",
                top: panelTop,
                left: "50%",
                transform: "translateX(-50%)",
                width: "90vw",
                maxWidth: 600,
                minWidth: 320,
                maxHeight:
                  typeof panelMaxHeight === "number"
                    ? `${panelMaxHeight}px`
                    : panelMaxHeight,
                background: "#fff",
                border: "1px solid #e5e7eb",
                boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                borderRadius: 12,
                overflow: "hidden",
                zIndex: 1000, // entre overlay (999) e barra (1001)
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  maxHeight:
                    typeof panelMaxHeight === "number"
                      ? `${panelMaxHeight - 40}px`
                      : panelMaxHeight,
                  overflow: "auto",
                }}
              >
                {typeof renderContent === "function" ? (
                  renderContent({
                    query: value,
                    setQuery: setValue,
                    close: closePanel,
                    isExpanded,
                  })
                ) : ContentComponent ? (
                  <ContentComponent
                    {...contentProps}
                    query={value}
                    close={closePanel}
                  />
                ) : (
                  <EmptyState query={value} />
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

function EmptyState({ query }) {
  return (
    <div style={{ padding: 16, fontSize: 14, color: "#475569" }}>
      {query?.length ? (
        <div>
          Nenhum componente de conteúdo foi conectado. Query atual:{" "}
          <strong>{query}</strong>
        </div>
      ) : (
        <div>Digite para pesquisar…</div>
      )}
    </div>
  );
}

function LupaIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m21 21-3.5-3.5" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FecharIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
